import { DarwinAgent, PromptProto, DarwinTool } from "build-ts/darwin_agent";
import showToast from "hooks/useCustomToast";
import { getCustomAgent } from "./CustomAgentServices";
import { getTreeById } from "./AnalysisTreeServices";
import { getPlan } from "./AnalysisPlanServices";
import { getScheduledJobRun } from "./ScheduleServices";
import { ChatClientType } from "build-ts/chat_session";
import { AnalysisTreeProto } from "build-ts/analysis";
import { AnalysisPlanProto } from "build-ts/analysis_plan";
import { CustomAgentProto } from "build-ts/custom_agent";
import { JinjaEnv } from "build-ts/jinja";

export const getAgentsList = async (params: URLSearchParams) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT_LIST}?${params}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to fetch agents list: ${errorMessage}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching agents list:", error);
        showToast("Failed to fetch agents list", error?.message, "error");
        // Re-throw error for table components to handle with error pages
        throw error;
    }
};

export const getAgentById = async (agentId: string) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT}/${agentId}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to fetch agent data: ${errorMessage}`);
        }

        return await response.json();
    } catch (error) {
        console.error("Error fetching agent data:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to fetch agent data", errorMessage, "error");
        return null;
    }
};

export const createNewAgent = async (agentData: DarwinAgent) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(agentData),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to create agent: ${errorMessage}`);
        }

        return await response.json();
    } catch (error) {
        console.error("Error creating agent:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to create agent", errorMessage, "error");
        return null;
    }
};

export const updateAgent = async (agent_id: string | undefined, agentConfig: DarwinAgent, selectedTools: DarwinTool[]) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT}/${agent_id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                header: {
                    name: agentConfig.header?.name,
                    description: agentConfig.header?.description,
                    is_active: agentConfig.header?.is_active,
                    version: agentConfig.header?.version,
                },
                tools: selectedTools.map(tool => ({ header: { id: tool.header?.id } })),
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to update agent: ${errorMessage}`);
        }

        const data = await response.json();
        showToast("Agent updated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error updating agent:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to update agent", errorMessage, "error");
        return null;
    }
};

export const duplicateAgents = async (agent_ids: string[]) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT_DUPLICATE}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ agent_ids }),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to duplicate agents: ${errorMessage}`);
        }

        const data = await response.json();
        showToast("Agent(s) duplicated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error duplicating agents:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to duplicate agents", errorMessage, "error");
        return null;
    }
};

export const deleteAgent = async (agentId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_AGENT}/${agentId}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete the agent: ${response.statusText}`);
    }

    return true;
};

export const getToolsList = async (params: URLSearchParams) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_TOOL_LIST}?${params}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to fetch tools list: ${errorMessage}`);
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching tools list:", error);
        showToast("Failed to fetch tools list", error?.message, "error");
        // Re-throw error for table components to handle with error pages
        throw error;
    }
};

export const updateTool = async (toolId: string | undefined, toolData: DarwinTool) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_TOOL}/${toolId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                header: {
                    name: toolData.header?.name,
                    description: toolData.header?.description,
                }
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to update tool: ${errorMessage}`);
        }

        const data = await response.json();
        showToast("Tool updated successfully", "", "success");
        return data;
    } catch (error: any) {
        console.error("Error updating tool:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to update tool", errorMessage, "error");
        return null;
    }
};

// prompt list api
export const getAssignedPrompts = async (agentId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/agent/${agentId}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to fetch assigned prompts: ${response.statusText}`);
    }

    return await response.json();
};

// Get Prompt Detail by Id
export const getPromptByid = async (promptId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/${promptId}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to fetch the prompt: ${response.statusText}`);
    }

    return await response.json();
};

// Update a Prompt
export const updatePrompt = async (promptId: string, promptData: PromptProto) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/${promptId}`, {
        method: "PUT",
        headers: {
        "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
            header: {
                description: promptData.header?.description,
                is_active: promptData.header?.is_active,
            },
            template: promptData.template,
            model_config: {
                model_name: promptData.model_config?.model_name,
                model_provider: promptData.model_config?.model_provider,
                temperature: promptData.model_config?.temperature,
            },
        }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to update the prompt: ${response.statusText}`);
    }

    return await response.json();
};

// Duplicate a Prompt
export const duplicatePrompt = async (promptId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/${promptId}/duplicate`, {
        method: "POST",
        credentials: "include",
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to duplicate the prompt: ${response.statusText}`);
    }

    return await response.json();
};

// Delete a Prompt
export const deletePrompt = async (promptId: string) => {
    const response = await fetch(`${process.env.REACT_APP_NEW_EVA_PROMPT}/${promptId}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete the prompt: ${response.statusText}`);
    }

    return true;
};

// Get System Prompt Documentation
export const getSystemPromptDocumentation = async (): Promise<JinjaEnv | null> => {
    try{
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_DOC}`, {
            method: "GET",
            credentials: "include",
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(`Failed to fetch documentation: ${errorMessage}`);
        }
        const data = await response.json();

        return JinjaEnv.fromJSON(data);
    } catch (error: any) {
        console.error("Error fetching documentation:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Failed to fetch documentation", errorMessage, "error");
        return null;
    }
};

// Validate Template Syntax
export const validateTemplateSyntax = async (content: string, sampleData: Record<string, any> = {}) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_NEW_EVA_TEMPLATE_VALIDATE}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                content,
                sample_data: sampleData
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to validate template syntax: ${response.statusText}`);
        }
        return await response.json();
    } catch (error: any) {
        console.error("Error validating template syntax:", error);
        const errorMessage = error?.message || "Unknown error occurred";
        showToast("Validation Failed", errorMessage, "error");
        return null;
    }
};

// Generate System prompt using Agent API
export const generateSystemPrompt = async (requestData: {
    darwinAgentId: string;
    promptId: string;
    trees?: AnalysisTreeProto[];
    plans?: AnalysisPlanProto[];
    customAgents?: CustomAgentProto[];
}) => {
    try {
        const chatRequest = {
            client_type: ChatClientType.EVA,
            agent_header: {
                id: requestData.darwinAgentId
            },
            prompt_header: {
                id: requestData.promptId
            },
            agent_input: {
                trees: requestData.trees || [],
                plans: requestData.plans || [],
                custom_agents: requestData.customAgents || []
            }
        };

        const response = await fetch(`${process.env.REACT_APP_CHAT_SESSION}/initialize`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(chatRequest),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(errorMessage);
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error generating system prompt:", error);
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        showToast("Failed to generate system prompt", errorMessage, "error");
        return null;
    }
};

// Fetch data for selected items from each step
export const fetchStepData = async (stepType: string, selectedIds: string[]) => {
    try {
        switch (stepType) {
            case "darwin_agents":
                // No need to fetch agent data - API only needs IDs
                return selectedIds.map(id => ({ id, name: `Agent ${id}` }));
            case "prompts":
                // No need to fetch prompt data - API only needs IDs
                return selectedIds.map(id => ({ id, name: `Prompt ${id}` }));
            case "analyses":
                // Fetch analysis trees using Newton API
                return await Promise.all(selectedIds.map(async id => {
                    const tree = await getTreeById(id, true, false, false, false);
                    return tree || null;
                }));
            case "plans":
                // Fetch analysis plans using Newton API
                return await Promise.all(selectedIds.map(async id => {
                    const plan = await getPlan(id);
                    return plan || null;
                }));
            case "custom_agents":
                // Fetch custom agents using Newton API
                return await Promise.all(selectedIds.map(async id => {
                    const custom_agents = await getCustomAgent(id);
                    return custom_agents || null;
                }));
            case "agent_runs":
                // Fetch custom agent runs using Newton API
                return await Promise.all(selectedIds.map(async id => {
                    const jobRun = await getScheduledJobRun(id);
                    return jobRun || null;
                }));
            default:
                return [];
        }
    } catch (error) {
        console.error(`Error fetching ${stepType} data:`, error);
        return [];
    }
};
